/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   ReadinessBadge.tsx
 *     Description     :   ReadinessBadge.tsx — the Spec 236 per-entity readiness pill.
 *                         Maps a readiness verdict to a kit Badge variant (Principle XLV — no hand-rolled colors).
 *                         A not-evaluated entity (null status) renders NotCollected — "Readiness unknown", never green.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-10
 *     Modified        :   2026-10-30 | Peter Dobler | Spec 236 T053: standard header (Principle XXXVII)
 * =========================================================================================
 */

import React from 'react';
import { Badge, NotCollected } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui/base/Badge';
import type { ReadinessStatus } from '../../api/backupReadiness';

const META: Record<ReadinessStatus, { label: string; variant: BadgeVariant }> = {
  ready: { label: 'Ready', variant: 'success' },
  empty_expected: { label: 'Empty (expected)', variant: 'default' },
  at_risk: { label: 'At risk', variant: 'warning' },
  blocked: { label: 'Blocked', variant: 'danger' },
  unknown: { label: 'Readiness unknown', variant: 'info' },
};

export const ReadinessBadge: React.FC<{ status: ReadinessStatus | null | undefined }> = ({ status }) => {
  if (!status) {
    return <NotCollected reason="Readiness not evaluated yet — no readiness pass has run for this entity" />;
  }
  const m = META[status] ?? META.unknown;
  return <Badge variant={m.variant}>{m.label}</Badge>;
};

export default ReadinessBadge;
